<template>
  <div>
    <Head :title="'Profissional: ' + professional.user.name" />
    <div class="w-full p-6">
      <div class="mb-14 lg:flex gap-11">
        <div v-if="professional.user?.photo?.url" class="w-[150px] mx-auto">
          <img :src="professional.user.photo.url" class="rounded-full w-[150px] h-[150px] mb-3 object-contain" />
        </div>

        <div class="flex-1">
          <h1 class="mb-1 text-xl font-roboto text-primary font-semibold">{{ professional.user.name }}</h1>
          <p class="text-lg text-gray-500 font-poppins">Especialidades: {{ professional.specialties_labels }}</p>

          <div class="w-full my-5 gap-[50px] sm:flex">
            <p class="text-xl font-poppins font-semibold text-primary">
              <star-rating :rating="professional.statistics.stars" :read-only="true" :increment="0.01" :star-size="20" />
            </p>

            <p class="text-xl font-poppins font-normal text-gray-500">
              <span class="font-semibold text-primary">{{ professional.statistics.reviewscount }}</span>
              Avaliações
            </p>

            <p class="text-xl font-poppins font-normal text-gray-500">
              <span class="font-semibold text-primary">{{ professional.statistics.appointmentscount }}</span>
              Consultas
            </p>
          </div>

          <p class="text-base font-poppins text-gray-500">{{ professional.about }}</p>
        </div>
      </div>

      <div class="w-full">
        <tabs
          :selectedItemIndex="selectedItemIndex"
          :items="tabItems"
          itemClass="px-4"
          class-head="md:w-[340px] mx-auto">
            <template #content.dashboard>
              <div>
                <div class="flex flex-wrap max-w-7xl m-auto my-0">
                  <div class="mb-5 lg:mb-10 w-full lg:w-5/12 lg:pr-6">
                    <div class="w-full text-center text-xl mb-6">Consultas por especialidade</div>
                    <doughnut-chart class="my-6" :chartData="this.chartDoughnutData()" :chartOptions="chartDoughnutOptions" />
                  </div>
                  <div class="mb-10 w-full lg:w-7/12">
                    <div class="w-full text-center text-xl mb-6 font-poppins">Agendamentos</div>
                    <progress-chart :totalMonth="totalMonth" :totalDay="totalDay" :totalInProgress="totalInProgress" :totalCompleted="totalCompleted" :totalNotCompleted="totalNotCompleted" :totalReschedule="totalReschedule" />
                  </div>
                </div>

                <div class="w-full mt-11">

                  <select-input class="lg:max-w-xs lg:inline-block" v-model="barChartFilters.selectedChartView">
                    <option value="profitability">Rentabilidade Mensal</option>
                    <option value="appointments">Quantidade de Atendimentos</option>
                  </select-input>

                  <date-month-input class="mt-4 lg:max-w-xs lg:inline-block lg:ml-4" :clearable="false" placeholder="Mês" v-model="barChartFilters.date" />

                  <h2 class="text-xl font-poppins font-normal my-8">{{ titleChart }}</h2>

                  <template v-if="chartBarData">
                    <div class="w-full max-w-[330px] sm:inline-block">
                      <p class="text-sm font-normal text-gray-500 capitalize">{{ monthDate }}</p>
                      <p class="text-2xl font-poppins font-semibold text-primary mt-1">{{ priceFormat( chartBarData.totalMonth ) }}</p>
                      <p class="text-xs font-poppins font-normal text-gray-500 mt-1">{{ legendMonth }}</p>
                    </div>

                    <div class="w-full max-w-[330px] text-gray-500 mt-6 sm:inline-block sm:ml-20">
                      <p class="text-sm font-normal">{{ yearDate }}</p>
                      <p class="text-2xl font-poppins font-semibold mt-1">{{ priceFormat( chartBarData.totalYear ) }}</p>
                      <p class="text-xs font-poppins font-normal mt-1">{{ legendYear }}</p>
                    </div>

                    <v-bar-chart :dataset="chartBarData.dataset" :label="chartLabel" />
                  </template>
                </div>
              </div>
            </template>

            <template #content.profile>
              <profile
                :addresses="professional.address"
                :next-date="professional.nextDate"
                :certificates="professional.certificates"
                @view-schendule="selectedItemIndex = 2" />
            </template>

            <template #content.schendule>
              <schendule
                :professional-id="professional.id"
                @view-patient="handleViewPatient"
                :specialtys="specialty" />
            </template>

            <template #content.reviews>
              <reviews
                :professional-id="professional.id"
                @view-patient="handleViewPatient" />
            </template>
        </tabs>
      </div>

    </div>
  </div>

  <modal class="lg:min-w-[900px]" ref="modal">
    <template #body>
      <button type="button" class="text-gray-500 absolute right-4 mr-4" @click="this.$refs.modal.close()">
        <font-awesome-icon icon="fa-x" />
      </button>

      <h2 class="text-xl font-normal font-poppins">Visualizar Paciente</h2>

      <ul class="w-full overflow-y-auto lg:grid grid-cols-2">
        <li v-for="[ key, val ] in Object.entries( patient )">
          <h3 class="text-base font-normal font-poppins my-6 capitalize">{{ key }}</h3>
          <p class="text-sm font-normal font-poppins mt-4 text-gray-500">{{ val }}</p>
        </li>
      </ul>
    </template>
  </modal>

</template>

<script>

import { computed } from 'vue';
import { Head, usePage } from '@inertiajs/inertia-vue3'
import starRating from 'vue-star-rating'

import Layout from '@/Shared/Layout/Layout'
import LineChart from '@/Shared/Components/LineChart'
import DoughnutChart from '@/Shared/Components/DoughnutChart'
import ProgressChart from '@/Shared/Components/ProgressChart'

import SelectInput from '@/Shared/Fields/SelectInput'
import DateMonthInput from '@/Shared/Fields/DateMonthInput'
import VBarChart from '@/Shared/Components/VBarChart'

import Tabs from '@/Shared/Components/Tabs/Tabs'
import Reviews from './Tabs/Reviews'
import Schendule from './Tabs/Schendule'
import Profile from './Tabs/Profile'

import Modal from '@/Shared/Modal/Modal'
import axios from 'axios';

const tabItems = [
  {
    idTab: 'dashboard',
    title: 'Dashboard',
  },
  {
    idTab: 'profile',
    title: 'Perfil',
  },
  {
    idTab: 'schendule',
    title: 'Agenda',
  },
  {
    idTab: 'reviews',
    title: 'Avaliações',
  },
]

export default {
  components: {
    Head,
    LineChart,
    DoughnutChart,
    ProgressChart,
    Tabs,
    SelectInput,
    DateMonthInput,
    VBarChart,
    starRating,
    Reviews,
    Schendule,
    Modal,
    Profile,
  },
  props: {
    professional: Object,
    totalDay: 0,
    totalMonth: 0,
    totalInProgress: 0,
    totalCompleted: 0,
    totalNotCompleted: 0,
    totalReschedule: 0,
    totalBySpecialty: Object,
    totalByMonth: Object,
    specialty: Array,
  },
  provide() {
    return {

    }
  },
  // mounted() {
  //
  // },
  data() {
    return {
      tabItems,
      patient: null,
      selectedItemIndex: 0,
      barChartFilters: {
        date: null,
        selectedChartView: 'profitability',
      },
      chartBarData: null,
      chartDoughnutOptions: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: 150,
        plugins: {
            legend: {
                position: 'bottom',
            }
        }
      }
    }
  },
  layout: Layout,

  mounted() {
    const date = new Date()

    this.barChartFilters.date = `${ date.getFullYear() }-${ date.getMonth() + 1 }`
    this.getChartBarData()
  },

  computed: {
    titleChart() {
      return this.barChartFilters.selectedChartView == 'profitability' ? 'Rentabilidade Mensal' : 'Quantidade de Atendimentos'
    },

    legendMonth() {
      return this.barChartFilters.selectedChartView == 'profitability' ? 'Lucro do mês atual' : 'Atendimentos do mês atual'
    },

    legendYear() {
      return this.barChartFilters.selectedChartView == 'profitability' ? 'Lucro do ano atual' : 'Atendimentos do ano atual'
    },

    chartLabel() {
      return this.barChartFilters.selectedChartView == 'profitability' ? 'Lucro' : 'Atendimentos'
    },

    monthDate() {
      const dateSplited = this.barChartFilters.date.split( '-' )
      const date = new Date( dateSplited[ 0 ], Number( dateSplited[ 1 ] ) - 1 )

      return date.toLocaleDateString( 'pt-BR', { month: 'long' } ) + '/' + date.getFullYear()
    },

    yearDate() {
      return this.barChartFilters.date.split( '-' )[ 0 ]
    },
  },

  watch: {
    'barChartFilters.selectedChartView': function () {
      this.getChartBarData()
    },

    'barChartFilters.date': function () {
      this.getChartBarData()
    }
  },

  methods: {
    handleViewPatient: async function ( id ) {
      const response = await axios.get( '/patients/profile', { params: { id } } )

      this.patient = response.data
      this.$refs.modal.open()
    },


    getChartBarData: async function () {
      const { selectedChartView: view, date } = this.barChartFilters

      const response = await axios.get( '/professionals-dashboard/chart-data/' + this.$props.professional.id, {
        params: { view, date }
      } )

      this.chartBarData = response.data
    },

    priceFormat( number ) {
      return this.barChartFilters.selectedChartView == 'profitability' ? 'R$ ' + number : number
    },

    chartDoughnutData() {
      let labels = [];
      let colors = [];
      let values = [];
      for(let index in this.totalBySpecialty) {
        let specialty = this.totalBySpecialty[index];
        labels.push(specialty.name);
        colors.push(specialty.color);
        values.push(specialty.appointments);
      }
      return {
        labels: labels,
        datasets: [
          {
            backgroundColor: colors,
            data: values
          }
        ]
      }
    },
    chartLineData() {
      var style = getComputedStyle(document.body);
      var colorPrimary = style.getPropertyValue('--color-primary');
      var colorSecondary = style.getPropertyValue('--color-secondary');
      let labels = [];
      let values = [];
      for(let index in this.totalByMonth) {
        let v = this.totalByMonth[index];
        let month = index.split('-').reverse().join('/');
        labels.push(month);
        values.push(v);
      }
      return {
        labels: labels,
        datasets: [
          {
            label: 'Agendamentos por mês',
            backgroundColor: colorPrimary,
            borderColor: colorSecondary,
            data: values
          }
        ]
      }
    }
  },
  setup() {
    const user = computed(() => usePage().props.value.auth.user)
    return { user }
  },
}
</script>
